"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCursorList } from "./use-cursor-list";
import { useTenancy } from "@/lib/tenancy/tenancy-context";
import { archiveTarget, listTargets, getTarget, listAllTargets } from "@/lib/api/targets";
import type { ListQuery } from "@/lib/api/types";

export function useTargets(params: Omit<ListQuery, "projectId"> = {}) {
  const { projectId } = useTenancy();
  return useQuery({
    queryKey: ["targets", projectId, params],
    queryFn: () => listTargets({ projectId, ...params }),
    enabled: !!projectId,
  });
}

/** Every target in the project (2.668): for counts and pickers that present the list as complete. */
export function useAllTargets() {
  const { projectId } = useTenancy();
  return useQuery({
    queryKey: ["targets", projectId, "all"],
    queryFn: () => listAllTargets(projectId),
    enabled: !!projectId,
  });
}

export function useTarget(id: string) {
  const { projectId } = useTenancy();
  return useQuery({
    queryKey: ["target", projectId, id],
    queryFn: () => getTarget(projectId, id),
    enabled: !!projectId && !!id,
  });
}


/**
 * The full target list, paged forward by cursor.
 *
 * `useTargets` remains for callers that genuinely want one bounded page — a
 * picker, a count on a dashboard tile — where following every cursor would
 * fetch a tenant's whole inventory to render a number.
 */
export function useTargetList(params: Omit<ListQuery, "projectId" | "cursor"> = {}) {
  const { projectId } = useTenancy();
  return useCursorList(
    ["targets", "list", projectId, params],
    (cursor) => listTargets({ projectId, ...params, cursor }),
    { enabled: !!projectId },
  );
}

/** FR-14a, ADR-088 — archive a target (irreversible). */
export function useArchiveTarget(id: string) {
  const { projectId } = useTenancy();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => archiveTarget(projectId, id),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["target", projectId, id] });
      void queryClient.invalidateQueries({ queryKey: ["targets"] });
    },
  });
}
